import { useCallback, useEffect, useRef, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { resendOutbound } from 'services/escriturasOutboundService';
import { clientsDetailI18n } from '../../i18n';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) => (e && typeof e === 'object' && 'target' in e ? e.target.value : e);

/**
 * Modal de reenvío de un mensaje saliente fallido/rebotado (R4 + R5).
 *
 * Compartido por la vista global "Log de Correos" y la línea de estado por
 * escrito en EEPP BETA. Muestra el destinatario actual + inputs editables de
 * Email y/o WhatsApp (prellenados con `message.toIdentifier` según el canal).
 * Al confirmar, corrige el contacto del cliente en el CRM y reenvía
 * (`resendOutbound`). En éxito notifica al padre con el nuevo DTO y se cierra.
 *
 * Máquina de estados (espejo de SendToNotaryModal): idle → sending → error.
 * El éxito cierra el modal (no hay pantalla de éxito propia; el padre refresca).
 *
 * Props:
 *   { DesignSystemComponents, show, message, onClose, onResent }
 *   - message: OutboundMessageDto (al menos { id, channel, toIdentifier }).
 *   - onResent(newDto): callback en éxito con el DTO del nuevo envío.
 */
export default function ResendOutboundModal({
  DesignSystemComponents,
  show,
  message,
  onClose,
  onResent,
}) {
  const { Button, Input } = DesignSystemComponents || {};
  const { t } = useScopedTranslation(clientsDetailI18n);

  const [phase, setPhase] = useState('idle'); // idle | sending | error
  const [errorMessage, setErrorMessage] = useState(null);
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const channel = (message?.channel || '').toLowerCase();
  const isEmail = channel === 'email';
  const isWhatsapp = channel === 'whatsapp';

  // Al abrir: reset + prefill del input del canal del mensaje con su destinatario.
  useEffect(() => {
    if (!show) return;
    setPhase('idle');
    setErrorMessage(null);
    setEmail(isEmail ? message?.toIdentifier || '' : '');
    setWhatsapp(isWhatsapp ? message?.toIdentifier || '' : '');
  }, [show, message?.toIdentifier, isEmail, isWhatsapp]);

  const runResend = useCallback(async () => {
    if (!message?.id) return;
    setPhase('sending');
    setErrorMessage(null);
    try {
      const trimmedEmail = email.trim();
      const trimmedWhatsapp = whatsapp.trim();
      const newDto = await resendOutbound(message.id, {
        email: trimmedEmail || undefined,
        whatsapp: trimmedWhatsapp || undefined,
      });
      if (!mountedRef.current) return;
      onResent?.(newDto);
      onClose?.();
    } catch (err) {
      if (!mountedRef.current) return;
      setErrorMessage(
        err.response?.data?.message ||
          err.message ||
          t('cd_eepp_beta_outbound_resend_error')
      );
      setPhase('error');
    }
  }, [message?.id, email, whatsapp, onResent, onClose, t]);

  if (!show || !message) return null;

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    zIndex: 1060,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: 10,
    width: '100%',
    maxWidth: 480,
    padding: 24,
    boxShadow: '0 20px 50px rgba(2, 6, 23, 0.25)',
  };

  const btnStyle = (variant) => ({
    border: variant === 'primary' ? '1px solid #2563EB' : '1px solid #CBD5E1',
    background: variant === 'primary' ? '#2563EB' : '#fff',
    color: variant === 'primary' ? '#fff' : '#475569',
    borderRadius: 6,
    padding: '6px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  });

  const sending = phase === 'sending';

  return (
    <div
      style={overlayStyle}
      role='dialog'
      aria-modal='true'
      onClick={() => {
        if (!sending) onClose?.();
      }}
    >
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h5 className='fw-semibold mb-2' style={{ fontSize: 15 }}>
          {t('cd_eepp_beta_outbound_resend_title')}
        </h5>

        <p className='font-light mb-3' style={{ color: '#475569', fontSize: 13 }}>
          {t('cd_eepp_beta_outbound_resend_current', {
            recipient: message.toIdentifier || '—',
          })}
        </p>

        {(isEmail || (!isEmail && !isWhatsapp)) && (
          <div className='mb-3'>
            {Input ? (
              <Input
                label={t('cd_eepp_beta_outbound_resend_email_label')}
                value={email}
                onChange={(e) => setEmail(readVal(e))}
                disabled={sending}
                placeholder='nombre@correo.cl'
                width='100%'
              />
            ) : (
              <>
                <label className='form-label fw-semibold' style={{ fontSize: 13 }}>
                  {t('cd_eepp_beta_outbound_resend_email_label')}
                </label>
                <input
                  type='email'
                  className='form-control'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={sending}
                  placeholder='nombre@correo.cl'
                  style={{ fontSize: 13 }}
                />
              </>
            )}
          </div>
        )}

        {isWhatsapp && (
          <div className='mb-3'>
            {Input ? (
              <Input
                label={t('cd_eepp_beta_outbound_resend_whatsapp_label')}
                value={whatsapp}
                onChange={(e) => setWhatsapp(readVal(e))}
                disabled={sending}
                placeholder='+56912345678'
                width='100%'
              />
            ) : (
              <>
                <label className='form-label fw-semibold' style={{ fontSize: 13 }}>
                  {t('cd_eepp_beta_outbound_resend_whatsapp_label')}
                </label>
                <input
                  type='text'
                  className='form-control'
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  disabled={sending}
                  placeholder='+56912345678'
                  style={{ fontSize: 13 }}
                />
              </>
            )}
          </div>
        )}

        <p className='mini mb-3' style={{ color: '#64748B' }}>
          {t('cd_eepp_beta_outbound_resend_note')}
        </p>

        {phase === 'error' && errorMessage && (
          <p className='mini mb-3' style={{ color: '#B91C1C' }}>
            {errorMessage}
          </p>
        )}

        <div className='d-flex justify-content-end' style={{ gap: 8 }}>
          {Button ? (
            <>
              <Button
                label={t('cd_eepp_beta_outbound_resend_btn_cancel')}
                variant='secondary'
                size='sm'
                disabled={sending}
                onClick={onClose}
              />
              <Button
                label={
                  sending
                    ? t('cd_eepp_beta_outbound_resend_btn_sending')
                    : t('cd_eepp_beta_outbound_resend_btn_confirm')
                }
                variant='primary'
                size='sm'
                disabled={sending}
                onClick={runResend}
              />
            </>
          ) : (
            <>
              <button type='button' style={btnStyle('secondary')} disabled={sending} onClick={onClose}>
                {t('cd_eepp_beta_outbound_resend_btn_cancel')}
              </button>
              <button
                type='button'
                style={{ ...btnStyle('primary'), opacity: sending ? 0.5 : 1 }}
                disabled={sending}
                onClick={runResend}
              >
                {sending
                  ? t('cd_eepp_beta_outbound_resend_btn_sending')
                  : t('cd_eepp_beta_outbound_resend_btn_confirm')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
